import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Sparkles, Heart, Shield, Sun } from 'lucide-react';
import { images } from '../../data/content';

const BookSection = () => {
  const { isRTL } = useLanguage();

  const bookContent = {
    tagline: isRTL ? 'الكتاب' : 'The Book',
    titleLine1: isRTL ? 'هل أنت إيجابي؟' : 'Are You a Positive Person?',
    titleLine2: isRTL ? 'كيف تصبح أحدهم' : 'How Can You Become One?',
    subtitle: isRTL 
      ? 'الإيجابية ليست مزاجًا — إنها مهارة يمكنك بناؤها.'
      : "Positivity is not a mood — it's a skill you can build.",
    description: isRTL
      ? 'الإيجابية هي قدرتنا على التعرف على الفرص في خضم الصعوبات — وتحويل التحديات إلى لحظات من النمو والتعلم. إنها القوة التي تحفز التميز وتساعدنا على تحويل الفشل إلى خطوة للأمام. في هذا الكتاب، تدعوك منى بزاري في رحلة ملهمة وعملية لفهم ما تعنيه الإيجابية حقًا، وكيف تُبنى، وما الذي يحتاج إلى التغيير في التفكير والسلوك اليومي لتصبح جزءًا ثابتًا من حياتك.'
      : "Positivity is our ability to recognize opportunities in the middle of difficulty — and to turn challenges into moments of growth and learning. It is the force that motivates excellence and helps us transform failure into a step forward. In this book, Mona Bizzari invites you on an inspiring, practical journey to understand what positivity truly means, how it is built, and what needs to change in everyday thinking and behavior for it to become a stable part of your life.",
    additionalText: isRTL
      ? 'مكتوب مع تركيز قوي على التمكين والتطوير الشخصي، يقدم الكتاب رؤى واضحة وتأملات من الحياة الواقعية وخطوات عملية لإعادة تشكيل العقلية وبناء المرونة العاطفية وإحداث تغيير هادف ودائم — يوجه القراء نحو حياة أكثر قوة وإيجابية وإشباعًا.'
      : 'Written with a strong focus on empowerment and personal development, the book offers clear insights, real-life reflections, and practical steps to reshape mindset, build emotional resilience, and create meaningful, lasting change — guiding readers toward a more empowered, positive, and fulfilling life.',
    learnings: isRTL ? [
      'كيفية بناء الإيجابية كممارسة يومية، وليس دفعة مؤقتة',
      'كيفية تحويل التحديات إلى نمو',
      'أدوات لتقوية المرونة العاطفية',
      'كيفية تطوير الثقة والقيادة الذاتية'
    ] : [
      'How to build positivity as a daily practice, not a temporary boost',
      'How to turn challenges into growth',
      'Tools to strengthen emotional resilience',
      'How to develop confidence and self-leadership'
    ],
    cta1: isRTL ? 'استكشف الكتاب' : 'Explore the Book',
    cta2: isRTL ? 'اطلب نسخة / اعرف المزيد' : 'Request a Copy / Learn More',
    whatYouLearn: isRTL ? 'ما ستتعلمه' : "What you'll learn"
  };

  const learningIcons = [Sparkles, Heart, Shield, Sun];

  return (
    <section id="book" className="section-padding bg-charcoal-100 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute top-20 left-10 w-32 h-32 bg-gold/5 rounded-full blur-3xl hidden lg:block" />
      <div className="absolute bottom-20 right-10 w-48 h-48 bg-gold/5 rounded-full blur-3xl hidden lg:block" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        {/* Header - Two Line Title */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
          <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
            {bookContent.tagline}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-gold leading-tight mb-2 px-2">
            {bookContent.titleLine1}
          </h2>
          <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-gold/90 leading-tight mb-4 sm:mb-6 px-2">
            {bookContent.titleLine2}
          </h3>
          <div className="gold-divider" />
          <p className="text-cream text-base sm:text-lg lg:text-xl mt-6 sm:mt-8 italic px-2">
            "{bookContent.subtitle}"
          </p>
        </div>

        {/* Main Content - Two Column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
          {/* Book Cover Side - Using Real Image */}
          <div className={`${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
            <div className="relative max-w-sm mx-auto lg:max-w-md">
              {/* Glow effect behind book */}
              <div className="absolute inset-0 bg-gold/10 blur-2xl rounded-3xl transform scale-90" />
              
              {/* Book cover image */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={images.book.positivity}
                  alt="Positivity Book by Mona Bizzari"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                {/* Subtle overlay for premium feel */}
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 via-transparent to-transparent" />
              </div>
              
              {/* Shadow under book */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-4 bg-black/30 blur-xl rounded-full" />
            </div>
          </div>

          {/* Content Side */}
          <div className={`${isRTL ? 'lg:order-1' : 'lg:order-2'}`}>
            {/* Description */}
            <div className="space-y-4 sm:space-y-5 mb-8">
              <p className="text-cream-300 leading-relaxed text-sm sm:text-base">
                {bookContent.description}
              </p>
              <p className="text-cream-300 leading-relaxed text-sm sm:text-base">
                {bookContent.additionalText}
              </p>
            </div>

            {/* What You'll Learn */}
            <div className="mb-8 sm:mb-10">
              <h4 className="text-gold text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-4 sm:mb-6">
                {bookContent.whatYouLearn}
              </h4>
              <div className="space-y-3 sm:space-y-4">
                {bookContent.learnings.map((learning, index) => {
                  const Icon = learningIcons[index] || Sparkles;
                  return (
                    <div key={index} className="flex items-start gap-3 sm:gap-4 group">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border border-gold/30 bg-gold/5 group-hover:bg-gold/10 transition-colors flex-shrink-0 rounded">
                        <Icon size={16} className="text-gold sm:w-[18px] sm:h-[18px]" />
                      </div>
                      <span className="text-cream-300 text-sm sm:text-base leading-relaxed pt-1 sm:pt-1.5">{learning}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/book/positivity"
                className="btn-gold flex items-center justify-center gap-2 w-full sm:w-auto rounded"
              >
                <BookOpen size={18} />
                <span>{bookContent.cta1}</span>
              </Link>
              <Link
                to="/book/positivity#request"
                className="btn-outline-gold flex items-center justify-center gap-2 w-full sm:w-auto rounded"
              >
                <span>{bookContent.cta2}</span>
                <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookSection;
